import { useState } from "react";
import { startOfDay } from "date-fns";
import { Box, Stack, Typography } from "@mui/material";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import { useTranslate } from "src/locales";
import InlineDateSelect from "./inline-date-select";

type LabColumnMeta = {
  date: string;
  time: string;
  highlight?: boolean;
};

type LabValueCell = {
  value: string;
  status?: "high" | "normal";
  ref: string;
};

const MOCK_COLUMNS: LabColumnMeta[] = [
  { date: "11.02.2026", time: "08:45" },
  { date: "12.02.2026", time: "15:30" },
  { date: "13.02.2026", time: "21:00", highlight: true },
];

const MOCK_CRP_CELLS: LabValueCell[] = [
  { value: "12.5", status: "high", ref: "< 5.0" },
  { value: "8.2", status: "high", ref: "< 5.0" },
  { value: "4.8", status: "normal", ref: "< 5.0" },
];

const CHART_BAR_HEIGHTS_PX = [59, 77, 89];

function ResultsTableBlock({
  t,
  testName,
  columns,
  cells,
}: Readonly<{
  t: (k: string) => string;
  testName: string;
  columns: LabColumnMeta[];
  cells: LabValueCell[];
}>) {
  const colTemplate = `minmax(220px, 280px) repeat(${columns.length}, minmax(160px, 1fr))`;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        background: "#fff",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        borderRadius: 1,
        border: "1px solid #E2E8F0",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: colTemplate,
          background: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 3,
            borderRight: "2px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              color: "#475569",
              fontSize: 14,
              fontFamily: "Poppins",
              fontWeight: 400,
              lineHeight: "20px",
            }}
          >
            {t("nurse.patient-table.lab-request-detail.tests-column")}
          </Typography>
        </Box>
        {columns.map((col) => (
          <Box
            key={`${col.date}-${col.time}`}
            sx={{
              py: 2,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.25,
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Typography
              sx={{
                color: col.highlight ? "#2563EB" : "#475569",
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                textAlign: "center",
                fontFamily: "Nimbus Sans, Poppins, sans-serif",
              }}
            >
              {col.date}
            </Typography>
            <Typography
              sx={{
                color: "#94A3B8",
                fontSize: 10,
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "20px",
                textAlign: "center",
              }}
            >
              {col.time}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: colTemplate,
          borderTop: "1px solid #F1F5F9",
        }}
      >
        <Box
          sx={{
            py: 2.5,
            px: 3,
            borderRight: "2px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "#334155",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "20px",
              fontFamily: "Nimbus Sans, Poppins, sans-serif",
            }}
          >
            {testName}
          </Typography>
        </Box>
        {cells.map((cell, idx) => (
          <Box
            key={`cell-${idx}`}
            sx={{
              py: 2,
              px: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                color: cell.status === "high" ? "#DC2626" : "#16A34A",
                fontSize: 14,
                fontWeight: 700,
                lineHeight: "20px",
                textAlign: "center",
                fontFamily: "Nimbus Sans, Poppins, sans-serif",
              }}
            >
              {cell.value}
            </Typography>
            {cell.status === "high" && (
              <Box
                sx={{
                  px: 0.75,
                  py: 0.25,
                  background: "#FEF2F2",
                  borderRadius: "4px",
                }}
              >
                <Typography
                  sx={{
                    color: "#DC2626",
                    fontSize: 10,
                    fontWeight: 400,
                    lineHeight: "20px",
                    textTransform: "uppercase",
                    textAlign: "center",
                    fontFamily: "Poppins",
                  }}
                >
                  {t("nurse.patient-table.lab-request-detail.high")}
                </Typography>
              </Box>
            )}
            <Typography
              sx={{
                color: "#94A3B8",
                fontSize: 10,
                fontWeight: 400,
                lineHeight: "20px",
                textAlign: "center",
                fontFamily: "Nimbus Sans, Poppins, sans-serif",
              }}
            >
              {t("nurse.patient-table.lab-request-detail.ref-prefix")} {cell.ref}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TrendChartCard({
  t,
}: Readonly<{
  t: (k: string) => string;
}>) {
  const yLabels = ["40", "30", "20", "10", "5", "0"];
  const xLabels = [
    t("nurse.patient-table.lab-request-detail.chart-x-1"),
    t("nurse.patient-table.lab-request-detail.chart-x-2"),
    t("nurse.patient-table.lab-request-detail.chart-x-3"),
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        background: "#fff",
        boxShadow:
          "0px 10px 24px -8px rgba(42, 51, 70, 0.03), 0px 0px 0px 1px rgba(0, 0, 0, 0.07)",
        borderRadius: "12px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          minHeight: 46,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Typography
          sx={{
            color: "#0A0D14",
            fontSize: 12,
            fontFamily: "Poppins",
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {t("nurse.patient-table.lab-request-detail.chart-card-title")}
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          position: "relative",
          flex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              color: "#0A0D14",
              fontSize: 20,
              fontFamily: "Poppins",
              fontWeight: 400,
              lineHeight: "24px",
            }}
          >
            50
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                background: "#2D9F75",
                clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                flexShrink: 0,
              }}
            />
            <Typography
              component="span"
              sx={{
                color: "#2D9F75",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "Inter, Poppins, sans-serif",
              }}
            >
              14.5%
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "#868C98",
                fontSize: 12,
                fontFamily: "Poppins",
                fontWeight: 400,
              }}
            >
              {t("nurse.patient-table.lab-request-detail.trend-suffix")}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, minHeight: 180 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-end",
              pr: 0.5,
              pt: 0.5,
            }}
          >
            {yLabels.map((lbl) => (
              <Typography
                key={lbl}
                sx={{
                  color: "rgba(134, 140, 152, 0.5)",
                  fontSize: 8,
                  fontFamily: "Poppins",
                  textTransform: "uppercase",
                  letterSpacing: 0.16,
                  lineHeight: "12px",
                }}
              >
                {lbl}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              width: 1,
              alignSelf: "stretch",
              background: "rgba(10, 13, 20, 0.05)",
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "flex-end",
                gap: 2,
                px: 2,
                position: "relative",
              }}
            >
              {CHART_BAR_HEIGHTS_PX.map((h, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: h,
                      background: i === 2 ? "#38C296" : "#8FDFC6",
                      borderRadius: "2px 2px 0 0",
                      border: "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "0px -14px 21px rgba(255, 255, 255, 0.4) inset",
                    }}
                  />
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                pt: 0.75,
                borderTop: "1px solid rgba(0, 0, 0, 0.05)",
              }}
            >
              {xLabels.map((xl, xi) => (
                <Box key={xl} sx={{ flex: 1, textAlign: "center" }}>
                  <Typography
                    sx={{
                      color: xi === 2 ? "#0A0D14" : "rgba(134, 140, 152, 0.5)",
                      fontSize: 8,
                      fontFamily: "Poppins",
                      letterSpacing: 0.16,
                      lineHeight: "12px",
                    }}
                  >
                    {xl}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: { xs: 16, sm: 120 },
            top: 72,
            width: 80,
            background: "#fff",
            boxShadow:
              "0px 0px 0px 1px #F2F2F2, 0px 5px 10px -2.5px rgba(42, 51, 70, 0.27)",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 1, py: 0.75, display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Typography
              sx={{
                color: "#868C98",
                fontSize: 8,
                fontFamily: "Poppins",
                fontWeight: 500,
              }}
            >
              {t("nurse.patient-table.lab-request-detail.value-label")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  background: "#48CAA1",
                  borderRadius: "2px",
                  border: "0.5px solid #fff",
                  boxShadow: "0px 0.5px 3px rgba(0, 0, 0, 0.15)",
                }}
              />
              <Typography
                sx={{
                  color: "#0A0D14",
                  fontSize: 10,
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  lineHeight: "12px",
                }}
              >
                50
              </Typography>
              <Typography
                sx={{
                  color: "#2D9F75",
                  fontSize: 8,
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  lineHeight: 1.4,
                }}
              >
                {t("nurse.patient-table.lab-request-detail.normal")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: 1, py: 0.75, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <Typography
              sx={{
                color: "#868C98",
                fontSize: 8,
                fontFamily: "Poppins",
                fontWeight: 400,
              }}
            >
              {t("nurse.patient-table.lab-request-detail.chart-tooltip-date")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export type LabRequestDetailProps = {
  onClose: () => void;
};

export default function LabRequestDetail({ onClose }: Readonly<LabRequestDetailProps>) {
  const { t } = useTranslate();
  const [dateFrom, setDateFrom] = useState<Date>(() => startOfDay(new Date()));
  const [dateTo, setDateTo] = useState<Date>(() => startOfDay(new Date()));

  const testName = t("nurse.patient-table.lab-request-detail.test-name-crp");

  const mainHeader = (closeAction: () => void) => (
    <Box
      sx={{
        alignSelf: "stretch",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Typography
        sx={{
          color: "#0F172A",
          fontSize: { xs: 22, sm: 30 },
          fontFamily: "Inter, Poppins, sans-serif",
          fontWeight: 700,
          lineHeight: "36px",
          wordBreak: "break-word",
          minWidth: 0,
        }}
      >
        {t("nurse.patient-table.lab-request-detail.title")}
      </Typography>

      <ByzMuiButton
        variant="contained"
        onClick={closeAction}
        sx={{
          px: 1.25,
          py: 0.5,
          minWidth: 96,
          background: "#B01212",
          color: "#fff",
          fontSize: 13,
          fontFamily: "Poppins",
          fontWeight: 500,
          textTransform: "capitalize",
          lineHeight: "22px",
          boxShadow:
            "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)",
          "&:hover": { background: "#8f0e0e" },
        }}
      >
        {t("nurse.patient-table.lab-request-detail.close")}
      </ByzMuiButton>
    </Box>
  );

  const dateFilterCard = (
    <Box
      sx={{
        alignSelf: "stretch",
        p: 2,
        background: "#fff",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        borderRadius: "12px",
        border: "1px solid #E2E8F0",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Typography
        sx={{
          color: "#94A3B8",
          fontSize: 12,
          fontFamily: "Poppins",
          fontWeight: 700,
          lineHeight: "16px",
          letterSpacing: 0.6,
        }}
      >
        {t("nurse.patient-table.lab-request-detail.date-range-label")}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        sx={{ width: "100%", flexWrap: "wrap" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Typography
            sx={{
              color: "#94A3B8",
              fontSize: 11,
              fontFamily: "Poppins",
              fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            {t("nurse.patient-table.lab-request-detail.date-from")}
          </Typography>
          <InlineDateSelect value={dateFrom} maxDate={dateTo} onChange={setDateFrom} />
        </Box>
        <Typography
          sx={{
            display: { xs: "none", sm: "block" },
            color: "#94A3B8",
            fontWeight: 600,
            alignSelf: "center",
            pb: 0.5,
          }}
        >
          —
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Typography
            sx={{
              color: "#94A3B8",
              fontSize: 11,
              fontFamily: "Poppins",
              fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            {t("nurse.patient-table.lab-request-detail.date-to")}
          </Typography>
          <InlineDateSelect value={dateTo} minDate={dateFrom} onChange={setDateTo} />
        </Box>
      </Stack>
    </Box>
  );

  const body = (closeAction: () => void) => (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minHeight: "100%",
        pl: { xs: 2, sm: 4 },
        pr: { xs: 2, sm: 4 },
        pt: { xs: 4, sm: 6 },
        pb: { xs: 4, sm: 6 },
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 3,
        boxSizing: "border-box",
      }}
    >
      {mainHeader(closeAction)}
      {dateFilterCard}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
        <ResultsTableBlock
          t={t}
          testName={testName}
          columns={MOCK_COLUMNS}
          cells={MOCK_CRP_CELLS}
        />
        <TrendChartCard t={t} />
      </Box>
    </Box>
  );

  return body(onClose);
}
